<template>
  <div class="card-update-tags" v-click-outside="deselectAll">
    <card title="Edit Tags">
      <p>
        <info-box>
          <ul>
            <li>Click on the Add Tag button to create a tag</li>
            <li>Reposition tags by clicking and dragging</li>
            <li>Click on a tag to display the edit tag dialog box</li>
          </ul>
        </info-box>
      </p>
      <button
        v-if="selectedSlide._id"
        class="btn btn-success my-2 mr-2"
        @click="addInteractiveSlideTag(selectedSlide._id)"
      >add tag to slide</button>
      <button
        v-if="selectedSlide._id"
        class="btn btn-success my-2"
        @click="deselectAll"
      ><fa :icon="['fas', 'times']" /></button>
      <button v-else class="btn btn-primary my-2" @click="addTag">add tag</button>

       <div v-if="referenceTagsSlider.length > 0" class="my-2">
        <label>Change slider position</label>
        <toggle-button 
          v-model="page.verticalSlider" 
          :color="{checked: '#28a745', unchecked: '#28a745'}"
          :sync="true" 
          :labels="{checked: 'Vertical', unchecked: 'Horizontal'}"
          :width="100" 
          :height="30" 
        />
      </div>

      <div class="tags-block">
        <div 
          class="canvas-wrapper"
          ref="canvasWrapper"
          @mousemove="handleDrag($event)"
          :style="canvasStyle"
        >
          <!-- Slide Tag Layer -->
          <div class="slide-tag-layer" >
            <template v-for="tag in selectedInteractiveSlideTags" tag="div">
              <tag-action
                v-if="tag.type === 'action'"
                :key="tag._id"
                v-bind="tag"
                :class="{
                  active: selectedElement._id === tag._id,
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />

              <tag-basic
                v-if="tag.type === 'basic'"
                :key="tag._id + tag.type"
                v-bind="tag"
                :class="{
                  active: selectedElement._id === tag._id,
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />

              <tag-box
                v-if="tag.type === 'box'"
                :key="tag._id"
                v-bind="tag"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />

              <tag-rich
                v-if="tag.type === 'rich'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />

              <tag-reference
                v-if="tag.type === 'reference'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />

              <tag-reveal
                v-if="tag.type === 'reveal'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />

              <tag-trail
                v-if="tag.type === 'trail'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="slideTagLayerDragStart(tag._id, $event)"
                @pointerup.native.left.stop="slideTagLayerDragEnd(tag._id, $event)"
              />
            </template>
          </div>
          
          <!-- Tag Layer -->
          <div class="tag-layer" :class="{'tag-layer--inactive' : isSlideSelected}">
            <template v-for="tag in page.tags" tag="div">
              <tag-action
                v-if="tag.type === 'action'"
                :key="tag._id"
                v-bind="tag"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />

              <tag-basic
                v-if="tag.type === 'basic'"
                :key="tag._id"
                v-bind="tag"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />

              <tag-box
                v-if="tag.type === 'box'"
                :key="tag._id"
                v-bind="tag"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />

              <tag-reference
                v-if="tag.type === 'reference'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />

              <tag-rich
                v-if="tag.type === 'rich'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />

              <tag-reveal
                v-if="tag.type === 'reveal'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />

              <tag-basic-icon
                v-if="tag.type === 'basicIcon'"
                :key="tag._id"
                v-bind="tag"
                :ref="tag._id"
                :class="{
                  active: selectedElement._id === tag._id, 
                  inactive: selectedElement._id !== tag._id && selectedElement._id !== undefined
                }"
                @pointerdown.native.left.stop="handleDragStart(tag._id, $event)"
                @pointerup.native.left.stop="handleDragEnd(tag._id, $event)"
              />
            </template>
          </div>

          <reference-tag-slider 
            v-if="referenceTagsSlider.length > 0"
            :key="'referenceTagSliderKey' + page.verticalSlider"
            :isVertical="page.verticalSlider"
            :scaledAmount="scale"
            :tagRef="referenceTagsSlider"
            @click.native.stop="{}"
          />

          <div class="interactive-slides">
            <draggable
              tag="div"
              class="base-items-inner d-flex"
              v-model="interactiveSlides"
              v-bind="dragOptionsSlides"
              :group="{pull: false}"
              :key="selectedSlide._id"
            >
              <div
                v-for="slide in interactiveSlides"
                :key="slide._id"
                class="interactive-slides__slide"
                :class="[{'interactive-slides__slide--no-bg' : slide.asset}, {'interactive-slides__slide--inactive' : slide._id !== selectedSlide._id}]"
                @click.stop="editInteractiveSlide(slide._id)"
              >
                <div class="interactive-slides__label" v-html="slide.label[activeLanguage.code]"></div>
                <img
                  v-if="slide.asset"
                  :src="`${$store.state.assetsPath}${slide.asset.customThumbPath ? slide.asset.customThumbPath : slide.asset.thumbPath}`"
                  alt="">
              </div>
            </draggable>
            <button class="interactive-slides__slide interactive-slides__slide--add-btn" @click="addInteractiveSlide">add slide</button>
          </div>

          <div class="canvas-overlay" :class="{'canvas-overlay--slide-active': isSlideSelected}">
            <div v-if="selectedElementLabel" class="canvas-overlay__slide-label">{{ selectedElementLabel }}</div>
          </div>

          <div ref="canvas" class="canvas" :class="{'no-image' : page.backgroundImage === ''}" @click.stop="deselectAll">
            <img
              v-if="backgroundAssetType === 'image' || slideBgOverride"
              class="tagBg"
              :class="{'hidden' : page.backgroundImage === ''}"
              :src="`${$store.state.assetsPath}${slideBgOverride ? slideBgOverride : page.backgroundImage}`"
            >
            <video
              v-if="backgroundAssetType === 'video' && !slideBgOverride"
              class="tagBg"
              :class="{'hidden' : page.backgroundImage === ''}"
              :src="`${$store.state.assetsPath + page.backgroundImage}`"
              autoplay
              muted
              loop
            ></video>
          </div>
        </div>

        <tag-info-box v-model="selectedElement" @remove="removeElement" @updateSlide="updateSlide" :scale="scale" :is-slide-selected="isSlideSelected" />
        <tag-info-box v-model="selectedSlideElement" @remove="removeElement" @updateSlide="updateSlide" :scale="scale" :is-slide-selected="isSlideSelected" />
      </div>
    </card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import InfoBox from '@/components/InfoBox'
import TagAction from '@/components/TagAction'
import TagBasic from '@/components/TagBasic'
import TagBasicIcon from '@/components/TagBasicIcon'
import TagBox from '@/components/TagBox'
import TagFeatureText from '@/components/TagFeatureText'
import TagReveal from '@/components/TagReveal'
import TagRich from '@/components/TagRich'
import TagReference from '@/components/TagReference'
import TagInfoBox from '@/components/TagInfoBox'
import ReferenceTagSlider from '@/components/ReferenceTagSlider'
import draggable from 'vuedraggable'

export default {
  name: 'card-update-interactive-tags',

  components: {
    draggable,
    InfoBox,
    ReferenceTagSlider,
    TagAction,
    TagBasic,
    TagBasicIcon,
    TagBox,
    TagFeatureText,
    TagReveal,
    TagRich,
    TagReference,
    TagInfoBox
  },

  data() {
    return {
      draggingTag: {},
      isSlideSelected: false,
      isTagDraggable: false,
      isTagDragging: false,
      scale: 1,
      selectedElement: {},
      selectedSlide: {},
      selectedSlideElement: {},
      slideBgOverride: null,
    }
  },

  computed: {
    ...mapGetters([
      'activeLanguage',
      'page'
    ]),

    dragOptionsSlides () {
      return {
        animation: 120,
        group: "slides",
        disabled: false,
        ghostClass: "ghostly"
      }
    },

    backgroundAssetType () {
      if (this.page.backgroundImage.split('.').pop() === 'mp4') {
        return 'video'
      } else {
        return 'image'
      }
    },

    canvasStyle () {
      if (this.scale) {
        return {
          ['font-size']: (20 * this.scale) + 'px',
          ['height']: (1960 * this.scale) + 'px',
        }
      }
      return {}
    },

    highestId () {
      let ids = [0]

      this.page.tags.forEach((tag) => {
        if (!isNaN(tag._id)) {
          ids.push(tag._id)
        }        
      })

      this.page.interactiveSlides.forEach(slide => {
        if (!isNaN(slide._id)) {
          ids.push(slide._id)
        }        
      })

      this.page.interactiveSlides.forEach(slide => {
        slide.tags.forEach(tag => {
          if (!isNaN(tag._id)) {
            ids.push(tag._id)
          }  
        }) 
      })

      return (Math.max(...ids) + 1).toString()
    },

    interactiveSlides: {
      get: function () {
        return this.page.interactiveSlides
      },

      set: function (newValue) {
        this.page.interactiveSlides = newValue
      }
    },

    selectedInteractiveSlideTags () {
      const selectedSlideId = this.selectedSlide._id
      let slide = _.find(this.page.interactiveSlides, slide => {
        return slide._id === selectedSlideId
      })

      if (slide !== undefined) {
        return slide.tags
      } else {
        return []
      }
    },

    referenceTagsSlider () {
      let refTags = this.page.tags.filter(function(tag) {
        return tag.type == "reference"
      })

      refTags = _.orderBy(refTags, ['overrideOrder', 'pos.x'], ['asc'])

      if (this.activeLanguage.rtl) {
        let startingIndex = refTags.length

        refTags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = startingIndex--
          }
        })
        return refTags
      } else {
        refTags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = index + 1
          }
        })
        return refTags
      }
    },

    selectedElementLabel () {
      if (!this.selectedElement._id) { return '' }

      let label = ''
      if (this.selectedSlide._id) {
        label = '[SLIDE] ' + (this.selectedSlide.label.en ? this.selectedSlide.label.en : this.selectedSlide._id)
      }

      if (this.selectedElement.elementType !== 'slide') {
        if (this.selectedSlide._id) {
          label += ' > '
        }
        label += `[TAG] ${this.selectedElement.label.en}`
      }

      return label
    }
  },

  mounted() {
    window.addEventListener('resize', this.getScaledAmount)
    this.getScaledAmount()

    window.addEventListener('keydown', this.onKeyDown)
  },

  methods: {
    getScaledAmount() {
      // default size based on 3040x1960 - scale = 1
      let canvasWidth = this.$refs.canvasWrapper.getBoundingClientRect().width
      let scaled = canvasWidth / 3190
      this.scale = scaled
    },

    handleDragStart(tagId, e) {
      e.target.setPointerCapture(e.pointerId)

      this.isTagDraggable = true

      let tag = _.find(this.page.tags, tag => {
        return tag._id === tagId
      })

      if (tag === undefined) {
        tag = _.find(this.page.interactiveSlides, tag => {
          return tag._id === tagId
        })
      }

      this.draggingTag = tag
      this.selectedElement = tag
    },

    handleDrag(event) {
      // check if a tag is being dragged
      if (this.isTagDraggable) {
        this.isTagDragging = true

        const canvasWidth = this.$refs.canvas.offsetWidth
        const canvasHeight = this.$refs.canvas.offsetHeight

        // get tag element
        const draggingTagId = this.draggingTag._id

        let tag = _.find(this.page.tags, tag => {
          return tag._id === draggingTagId
        })

        if (tag === undefined) {
          tag = _.find(this.selectedInteractiveSlideTags, tag => {
            return tag._id === draggingTagId
          })
        }

        const tagEl = document.getElementById(draggingTagId).getBoundingClientRect()
        const tagElWidth = tagEl.width
        const tagElHeight = tagEl.height

        tag.pos.x = Math.round(tag.pos.x + event.movementX / this.scale)
        tag.pos.y = Math.round(tag.pos.y + event.movementY / this.scale)

        if (tag.pos.x < 0) {
          tag.pos.x = 0
        }

        if (tag.pos.x > canvasWidth / this.scale) {
          tag.pos.x = Math.round(canvasWidth / this.scale)
        }

        if (tag.pos.y < 0) {
          tag.pos.y = 0
        }

        if (tag.pos.y > canvasHeight / this.scale) {
          tag.pos.y = Math.round(canvasHeight / this.scale)
        }
      }
    },

    handleDragEnd(tagId, e) {
      e.target.releasePointerCapture(e.pointerId)

      this.showPopupModal('tag', tagId)

      this.isTagDragging = false
      this.isTagDraggable = false
    },

    addInteractiveSlide () {
      const newSlide = {
        _id: this.highestId,
        asset: { isEmpty: true },
        assetId: null,
        backgroundImageOverride: '',
        label: {
          en: ''
        },
        tags: [],
        elementType: 'slide'
      }

      this.page.interactiveSlides.push(newSlide)
    },

    editInteractiveSlide (id) {
      this.showPopupModal('slide', id)
    },

    addInteractiveSlideTag (id) {
      // get interactive slide
      let slide = _.find(this.page.interactiveSlides, slide => {
        return slide._id === id
      })

      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        slide.tags.push(res.data)
      })
    },

    addTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.page.tags.push(res.data)
      })
    },

    deselectAll () {
      console.log('deselect all')
      
      this.selectedElement = {}
      this.draggingTag = {}
      this.isTagDragging = false
      this.isTagDraggable = false

      this.selectedSlide = {}
      this.isTagDragging = false
      this.isTagDraggable = false
      this.isSlideSelected = false
      this.slideBgOverride = null
    },

    removeElement () {
      const elementId = this.selectedElement._id

      switch (this.selectedElement.elementType) {
        case 'tag':
          this.page.tags = this.page.tags.filter(tag => tag._id != elementId) 
          break;

        case 'slideTag':
          const remainingSlideTags = this.selectedSlide.tags.filter(tag => tag._id !== elementId)

          _.find(this.page.interactiveSlides, slide => {
            if (slide._id === this.selectedSlide._id) {
              slide.tags = remainingSlideTags
            }
          })
          break;

        case 'slide':
          const remainingInteractiveSlides = this.page.interactiveSlides.filter(function(slide) {
            if (slide._id != elementId) {
              return slide
            }
          })

          this.page.interactiveSlides = remainingInteractiveSlides
          break;
      
        default:
          break;
      }
      
      // deselect selection
      this.deselectAll()
    },

    showPopupModal(type, id) {
      let pageElement = {}

      if (type === 'tag') {
        pageElement = this.page.tags.find(tag => tag._id === id)

        this.selectedSlide = {}
        this.isSlideSelected = false

        this.selectedElement = pageElement
        this.selectedElement['elementType'] = 'tag'
      }

      if (type === 'slide') {
        pageElement = this.page.interactiveSlides.find(slide => slide._id === id)

        this.isSlideSelected = true
        this.slideBgOverride = pageElement.backgroundImageOverride
        this.selectedSlide = pageElement
        this.selectedElement = { ...pageElement, type: 'slide', elementType: 'slide' }
      }

      if (type === 'slidetag') {
        pageElement = this.selectedInteractiveSlideTags.find(tag => tag._id === id)

        this.selectedElement = { ...pageElement, elementType: 'slideTag' }
      }
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit('openAssetLibrary', context, assetSelected)
    },

    slideTagLayerDragStart (tagId, e) {
      e.target.setPointerCapture(e.pointerId)

      this.draggingTag = {}
      this.selectedElement = {}
      this.selectedSlideElement = {}

      this.isTagDraggable = true

      let tag = this.selectedInteractiveSlideTags.find(tag => tag._id === tagId)

      this.draggingTag = tag
      this.selectedSlideElement = tag
    },

    slideTagLayerDragEnd (tagId, e) {
      e.target.releasePointerCapture(e.pointerId)

      this.showPopupModal('slidetag', tagId)

      this.isTagDragging = false
      this.isTagDraggable = false
    },

    updateSlide (changedSlide) {
      let slide = _.find(this.page.interactiveSlides, slide => {
        return slide._id === changedSlide._id
      })

      slide.label = changedSlide.label
      slide.shouldSlideExpand = changedSlide.shouldSlideExpand
      slide.backgroundImageOverride = changedSlide.backgroundImageOverride
      slide.assetId = changedSlide.assetId
    },

    onKeyDown (e) {
      switch (e.keyCode) {
        case 27:
          this.deselectAll()
          break
                
        default:
          break

      }
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getScaledAmount)
    window.removeEventListener('keydown', this.onKeyDown)
  }
}
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@/styles/_mixins.scss';

.tags-block {
  overflow: hidden;
  position: relative;
}

.canvas-overlay {
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 5;

  &--slide-active {
    border: 5px solid #28a745;
  }

  &__slide-label {
    background: #3182ce;
    color: #fff;
    display: inline-block;
    font-size: 14px;
    padding: 2px 4px;

    .canvas-overlay--slide-active & {
      background: #28a745;
      padding: 0 4px 2px 0;
    }
  }
}

.canvas {
  background-position: 50% 50%;
  background-size: cover;
  height: 100%;
  position: absolute;
  user-select: none;
  width: 100%;
  z-index: 1;

  &.no-image {
    background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
    background-position: 0px 0px, 10px 10px;
    background-size: 20px 20px;
  }

  .tagBg {
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    width: 100%;

    &.hidden {
      opacity: 0;
    }
  }
}

.canvas-wrapper {
  margin: 0 0 5px;
  outline: 5px solid #fff;
  overflow: hidden;
  position: relative;
  transition: outline 300ms;
  width: 100%;
}

.card-update-tags {
  padding-right: 15px;
  padding-left: 15px;
  width: 100%;
}

.interactive-slides {
  align-items: flex-end;
  bottom: 5em;
  display: flex;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 20;

  &__slide {
    background: transparent;
    border-radius: .5em;
    box-shadow: $tag-box-shadow;
    display: inline-block;
    color: #ffffff;
    cursor: pointer;
    height: 8.25em;
    margin: 0 1em;
    overflow: hidden;
    pointer-events: auto;
    position: relative;
    transition: opacity 200ms ease;
    user-select: none;
    width: 12.5em;

    &--add-btn {
      appearance: none;
      background: transparent;
      box-shadow: none;
      border: .2em dashed rgba(255, 255, 255, .8);
      color: #ffffff;
    }

    &--no-bg {
      background-position: 0px 0px, 10px 10px;
      background-size: 20px 20px;
      background-image: 
      linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%),
      linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
    }

    &--inactive {
      opacity: .5;
    }

    img {
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      width: 100%;
    }
  }

  &__label {
    align-items: center;
    background: rgba(0, 0, 0, .6);
    bottom: 0;
    display: flex;
    font-size: .6em;
    font-weight: 300;
    justify-content: center;
    height: 2.1em;
    letter-spacing: .05em;
    padding: .2em .75em;
    position: absolute;
    text-transform: uppercase;
    width: 100%;
  }
}

.slide-tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 15;
}

.tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 10;

  &--inactive {
    opacity: .5;
  }
}
</style>
